<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">JIRA Issues</h1>
        <p class="mt-1 text-sm text-gray-600">
          Apache Kafka PMC 相关的 JIRA 问题
        </p>
      </div>
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <label class="text-sm font-medium text-gray-700">排序方式:</label>
          <select
            v-model="sortBy"
            @change="handleSortChange"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="updated">更新时间</option>
            <option value="created">创建时间</option>
          </select>
        </div>
        <div class="flex items-center space-x-2">
          <label class="text-sm font-medium text-gray-700">成员过滤:</label>
          <select
            v-model="memberFilter"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">所有成员</option>
            <option value="active">仅活跃成员</option>
            <option value="inactive">仅不活跃成员</option>
          </select>
        </div>
        <button
          @click="triggerSync"
          :disabled="loading || syncStatus.isRunning"
          class="btn btn-secondary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>{{ syncStatus.isRunning ? '同步中...' : '同步数据' }}</span>
        </button>
        <button
          @click="refreshData"
          :disabled="loading"
          class="btn btn-primary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>刷新</span>
        </button>
      </div>
    </div>

    <!-- 数据状态信息 -->
    <div class="card p-4 mb-4 bg-blue-50 border-blue-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <div class="text-sm text-gray-600">
            <span class="font-medium">数据源:</span> {{ dataSource.jira }}
          </div>
          <div class="text-sm text-gray-600">
            <span class="font-medium">监控策略:</span>
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              仅活跃PMC成员
            </span>
          </div>
          <div class="text-sm text-gray-600" v-if="lastUpdated.jira">
            <span class="font-medium">最后更新:</span> {{ formatTime(lastUpdated.jira) }}
          </div>
        </div>
        <div class="text-xs text-gray-500">
          数据来自{{ dataSource.jira.includes('缓存') ? '缓存' : '数据库' }}，点击"同步数据"获取最新信息
        </div>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="card p-4">
        <div class="text-2xl font-bold text-primary-600">{{ totalIssues }}</div>
        <div class="text-sm text-gray-600">总问题数</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-red-600">{{ openIssues }}</div>
        <div class="text-sm text-gray-600">开放问题</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-green-600">{{ resolvedIssues }}</div>
        <div class="text-sm text-gray-600">已解决</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-blue-600">{{ inProgressIssues }}</div>
        <div class="text-sm text-gray-600">进行中</div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-flex items-center space-x-2">
        <svg class="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">加载中...</span>
      </div>
    </div>

    <!-- Issues列表 -->
    <div v-else-if="sortedIssues.length > 0" class="space-y-4">
      <div
        v-for="issue in sortedIssues"
        :key="issue.key"
        class="card p-6 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-3 mb-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getStatusColor(issue.status)">
                {{ issue.status }}
              </span>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getPriorityColor(issue.priority)">
                {{ issue.priority }}
              </span>
              <span class="text-xs text-gray-500">{{ issue.issueType }}</span>
            </div>
            
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              <a :href="issue.url" target="_blank" class="hover:text-primary-600">
                {{ issue.key }}: {{ issue.summary }}
              </a>
            </h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <span class="font-medium">负责人:</span>
                {{ issue.assignee || '未分配' }}
              </div>
              <div>
                <span class="font-medium">报告人:</span>
                {{ issue.reporter }}
              </div>
              <div>
                <span class="font-medium">创建时间:</span>
                {{ formatDate(issue.created) }}
              </div>
              <div>
                <span class="font-medium">更新时间:</span>
                {{ formatDate(issue.updated) }}
              </div>
            </div>
          </div>
          
          <div class="ml-4 flex-shrink-0">
            <a
              :href="issue.url"
              target="_blank"
              class="btn btn-outline-primary btn-sm"
            >
              查看详情
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">暂无数据</h3>
      <p class="mt-1 text-sm text-gray-500">点击刷新按钮获取最新数据</p>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">加载失败</h3>
          <p class="mt-1 text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores'

const store = useAppStore()
const sortBy = ref('updated')
const memberFilter = ref('all')

// 使用 storeToRefs 保持响应式
const { loading, error, jiraIssues, totalIssues, lastUpdated, dataSource, syncStatus, defaultPMCMembers } = storeToRefs(store)

// 计算属性
const sortedIssues = computed(() => {
  if (!jiraIssues.value) return []

  let issues = [...jiraIssues.value]

  // 根据成员活跃度过滤
  if (memberFilter.value !== 'all') {
    const activeMemberIds = defaultPMCMembers.value
      .filter(member => member.activityStatus === 'active')
      .map(member => member.jiraId)
      .filter(id => id)

    const inactiveMemberIds = defaultPMCMembers.value
      .filter(member => member.activityStatus === 'inactive')
      .map(member => member.jiraId)
      .filter(id => id)

    if (memberFilter.value === 'active') {
      issues = issues.filter(issue =>
        activeMemberIds.includes(issue.assigneeKey) ||
        activeMemberIds.includes(issue.reporterKey)
      )
    } else if (memberFilter.value === 'inactive') {
      issues = issues.filter(issue =>
        inactiveMemberIds.includes(issue.assigneeKey) ||
        inactiveMemberIds.includes(issue.reporterKey)
      )
    }
  }

  // 排序
  return issues.sort((a, b) => {
    const dateA = new Date(sortBy.value === 'updated' ? a.updated : a.created)
    const dateB = new Date(sortBy.value === 'updated' ? b.updated : b.created)
    return dateB - dateA
  })
})

const openIssues = computed(() => {
  return jiraIssues.value.filter(issue => 
    issue.status === 'Open' || issue.status === 'Reopened'
  ).length
})

const resolvedIssues = computed(() => {
  return jiraIssues.value.filter(issue => 
    issue.status === 'Resolved' || issue.status === 'Closed'
  ).length
})

const inProgressIssues = computed(() => {
  return jiraIssues.value.filter(issue => 
    issue.status === 'In Progress' || issue.status === 'Patch Available'
  ).length
})

// 方法
const formatDate = (dateString) => {
  if (!dateString) return '未知'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = (now - date) / (1000 * 60 * 60)
  
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}小时前`
  } else if (diffInHours < 24 * 7) {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

const getStatusColor = (status) => {
  const colors = {
    'Open': 'bg-red-100 text-red-800',
    'Reopened': 'bg-red-100 text-red-800',
    'In Progress': 'bg-blue-100 text-blue-800',
    'Patch Available': 'bg-yellow-100 text-yellow-800',
    'Resolved': 'bg-green-100 text-green-800',
    'Closed': 'bg-gray-100 text-gray-800'
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

const getPriorityColor = (priority) => {
  const colors = {
    'Blocker': 'bg-red-100 text-red-800',
    'Critical': 'bg-red-100 text-red-800',
    'Major': 'bg-orange-100 text-orange-800',
    'Minor': 'bg-yellow-100 text-yellow-800',
    'Trivial': 'bg-gray-100 text-gray-800'
  }
  return colors[priority] || 'bg-gray-100 text-gray-800'
}

const handleSortChange = () => {
  // 排序变化时的处理逻辑
}

const refreshData = async () => {
  try {
    await store.fetchJiraIssues(true)
  } catch (error) {
    console.error('Failed to refresh JIRA data:', error)
  }
}

const triggerSync = async () => {
  try {
    await store.triggerDataSync('jira')
  } catch (error) {
    console.error('Failed to trigger sync:', error)
  }
}

// 格式化时间显示
const formatTime = (timeString) => {
  if (!timeString) return '未知'

  const time = new Date(timeString)
  const now = new Date()
  const diff = now - time

  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (minutes < 1) {
    return '刚刚'
  } else if (minutes < 60) {
    return `${minutes}分钟前`
  } else if (hours < 24) {
    return `${hours}小时前`
  } else if (days < 7) {
    return `${days}天前`
  } else {
    return time.toLocaleDateString('zh-CN')
  }
}

// 生命周期
onMounted(() => {
  if (jiraIssues.value.length === 0) {
    refreshData()
  }
})
</script> 